import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

export interface PortalConfig {
  title: string;
  backgroundImageUrl: string;
  logoUrl: string;
  modules: {
    analysis: { enabled: boolean };
    documentation: { enabled: boolean };
    transitSuite: { enabled: boolean };
  };
}

export interface ProgrammingAuthor {
  id: string;
  fullName: string;
}

export interface ProgrammingConfigResponse {
  config: Record<string, unknown> | null;
  isDefault: boolean;
  revision: number;
  frozenDataVersion: string | null;
  routeShapeFingerprint: string | null;
  lastEditedBy: ProgrammingAuthor | null;
  lastEditedAt: string | null;
  canWrite: boolean;
}

export interface ProgrammingHistoryItem {
  revision: number;
  author: ProgrammingAuthor;
  createdAt: string;
  frozenDataVersion: string;
  routeShapeFingerprint: string;
}

export interface ProgrammingHistoryDetail extends ProgrammingHistoryItem {
  config: Record<string, unknown>;
}

export type SaveProgrammingResult =
  | { ok: true; data: ProgrammingConfigResponse }
  | { ok: false; conflict: true; currentRevision: number | null; message: string }
  | { ok: false; conflict: false; message: string };

export interface PedestrianCoverageResult {
  method: 'CORREDOR' | 'PARADAS';
  walkTimeMinutes: number;
  walkingSpeedKmh: number;
  populationCovered: number;
  accessibleAreaKm2: number;
  accessNodeCount: number;
  meanAccessNodeSeparationM: number | null;
  maxAccessNodeSeparationM: number | null;
  stopsConsidered: number;
  populationCoveragePct: number | null;
  coveragePolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  territorialProjectFingerprint: {
    projectId: string;
    edgeCount: number;
    nodeCount: number;
    populationRunId: string;
    populationRunCompletedAt: string;
    populationMethod: string;
  };
}

export type RouteDirection = 'IDA' | 'VUELTA' | 'CIRCUITO';
export type PavementBucket = 'ASPHALT' | 'OTHER_PAVED' | 'UNPAVED' | 'UNKNOWN';

export interface PavementBucketBreakdown {
  bucket: PavementBucket;
  lengthM: number;
  pct: number;
  realSurfaceTypes: string[];
}

export interface PavementSegment {
  canonicalEdgeId: string;
  startM: number;
  endM: number;
  bucket: PavementBucket;
}

export interface MinimalPointFeature {
  id: string;
  lat: number;
  lon: number;
  name: string | null;
  osmSubtype: string | null;
  hasNetworkAssociation: boolean;
}

export type SyntaxMetricSlimKey =
  | 'integrationGlobal'
  | 'integrationGlobalRaw'
  | 'integrationR1200'
  | 'integrationR800'
  | 'choiceGlobal'
  | 'choiceR1200'
  | 'choiceR800'
  | 'structuralPriority';

export interface SyntaxMetricSlimEdge {
  edgeId: string;
  value: number | null;
}

export interface SyntaxMetricSlimResponse {
  metric: SyntaxMetricSlimKey;
  edges: SyntaxMetricSlimEdge[];
  hasMetrics: boolean;
}

export interface SyntaxRange {
  integrationGlobal: { min: number; max: number };
  integrationGlobalRaw: { min: number; max: number };
  integrationR1200: { min: number; max: number };
  integrationR800: { min: number; max: number };
  choiceGlobal: { min: number; max: number };
  choiceR1200: { min: number; max: number };
  choiceR800: { min: number; max: number };
  structuralPriority: { min: number; max: number };
}

export interface RouteMetricsResult {
  analysisId: string;
  direction: RouteDirection;
  routeMode: 'CIRCUIT' | null;
  edgeCount: number;
  totalLengthM: number;
  trafficSignals: { count: number; features: MinimalPointFeature[] };
  trafficCalming: { count: number; features: MinimalPointFeature[] };
  railwayCrossings: { count: number; features: MinimalPointFeature[] };
  streetlights: { count: number; features: MinimalPointFeature[] };
  crossings: { count: number; features: MinimalPointFeature[] };
  pavement: { totalLengthM: number; breakdown: PavementBucketBreakdown[]; segments: PavementSegment[] };
}

export type AreaAccessibilityTier =
  | 'ACCESSIBLE_VIA_ACCESS_POINT'
  | 'ACCESSIBLE_VIA_PERIMETER'
  | 'ACCESSIBLE_PERIMETER_OPEN'
  | 'GEOMETRICALLY_NEAR';

export interface MinimalAreaFeature {
  id: string;
  entityType: string;
  name: string | null;
  osmSubtype: string | null;
  tier: AreaAccessibilityTier;
  geometry: GeoJSON.MultiPolygon | GeoJSON.Polygon;
}

/** Ver `DemographicBreakdown` en el backend — reusa la misma ponderación auditada de `populationCovered`. Educación NO es mutuamente excluyente (niveles acumulativos), nunca sumar como torta de 100%. */
export interface DemographicBreakdown {
  radioCount: number;
  ageBrackets: Record<'pop_0_3' | 'pop_4_5' | 'pop_6_12' | 'pop_13_17' | 'pop_18_29' | 'pop_30_54' | 'pop_55_69' | 'pop_70_plus', number>;
  sex: { males: number; females: number };
  education: Record<'edu_primary_incomplete' | 'edu_primary_complete_plus' | 'edu_secondary_complete_plus' | 'edu_tertiary_complete_no_uni' | 'edu_university_complete', number>;
  households: Record<'households_total' | 'households_with_nbi' | 'households_overcrowding' | 'households_owner' | 'households_tenant', number>;
}

export interface CoverageEntitiesResult {
  analysisId: string;
  direction: RouteDirection;
  routeMode: 'CIRCUIT' | null;
  walkTimeMinutes: number;
  walkingSpeedKmh: number;
  populationCovered: number;
  populationCoveragePct: number | null;
  demographics: DemographicBreakdown | null;
  coveragePolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  education: { count: number; features: MinimalPointFeature[] };
  health: { count: number; features: MinimalPointFeature[] };
  civic: { count: number; features: MinimalPointFeature[] };
  greenSpace: { accessibleCount: number; nearCount: number; features: MinimalAreaFeature[] };
  sportsFacility: { accessibleCount: number; nearCount: number; features: MinimalAreaFeature[] };
  secondary: { count: number; byType: Record<string, number>; features: (MinimalPointFeature & { entityType: string })[] };
}

export interface ModelCVirtualStopAreaSummary {
  id: string;
  stableKey: string;
  primaryType: string | null;
  pairingConfidence: string | null;
  unresolved: boolean;
}

export interface ModelCComplexStopZoneSummary {
  id: string;
  stableKey: string;
  configurationType: string | null;
  trigger: string | null;
  exceptionType: string | null;
}

export interface ModelCServiceStop {
  sequence: number;
  facilityId: string;
  stableKey: string;
  displayName: string | null;
  streetName: string | null;
  crossStreetName: string | null;
  geometry: GeoJSON.Point;
  /** Solo para dibujo — desplazada a la derecha del sentido de circulación de la variante. */
  displayGeometry: GeoJSON.Point;
  canonicalEdgeId: string | null;
  positionOnEdge: number | null;
  facilityMeasureM: number;
  distanceFromPreviousM: number | null;
  distanceToNextM: number | null;
  virtualStopArea: ModelCVirtualStopAreaSummary | null;
  complexStopZone: ModelCComplexStopZoneSummary | null;
  sharedRoutesCount: number | null;
}

export interface ModelCServiceStopsResult {
  analysisId: string;
  direction: RouteDirection;
  routeMode: 'CIRCUIT' | null;
  routeCode: string;
  variantId: string;
  activeRunId: string;
  stops: ModelCServiceStop[];
  summary: {
    stopCount: number;
    averageSpacingM: number | null;
    minSpacingM: number | null;
    maxSpacingM: number | null;
  };
}

export type DayType = 'Hábil' | 'Sábado' | 'Domingo';

export const DAY_TYPES: readonly DayType[] = ['Hábil', 'Sábado', 'Domingo'];

/** Mismas 6 bandas municipales que el backend (`MUNICIPAL_BANDS`) — duplicadas acá porque el frontend nunca importa código del backend. */
export const MUNICIPAL_BANDS: readonly { start: string; end: string }[] = [
  { start: '05:00', end: '07:00' },
  { start: '07:00', end: '08:00' },
  { start: '08:00', end: '11:00' },
  { start: '11:00', end: '19:00' },
  { start: '19:00', end: '22:00' },
  { start: '22:00', end: '24:00' },
];

export interface LineCoverageSummary {
  populationCovered: number;
  populationCoveragePct: number | null;
  accessibleAreaKm2: number;
  accessNodeCount: number;
  method: 'CORREDOR' | 'PARADAS';
  walkingSpeedKmh: number;
}

export interface LineSummaryRow {
  analysisId: string;
  label: string;
  available: true;
  idaKm: number;
  vueltaKm: number;
  idaTimeMin: number | null;
  vueltaTimeMin: number | null;
  frequencyMin: number | null;
  cycleMin: number | null;
  /** Unidades para la banda horaria SELECCIONADA — nunca el máximo diario, ver `maxFleetRequired`. */
  fleetRequired: number | null;
  /** Máximo de unidades de la línea en algún momento del Día tipo seleccionado (todas las bandas) — mismo motor que "Flota requerida" de Programación. Depende de Día tipo, nunca de la banda seleccionada. */
  maxFleetRequired: number | null;
  configSource: 'saved' | 'default';
  bandCoveredByConfig: boolean;
  stopsStatus: 'not_loaded';
  coverage2min: LineCoverageSummary | null;
  coverage5min: LineCoverageSummary | null;
  computeError: string | null;
  /** `'CIRCUIT'` cuando IDA=VUELTA es la misma geometría (un solo camino) — `vueltaKm`/`vueltaTimeMin` no son un tramo real en ese caso. */
  routeMode: 'CIRCUIT' | null;
  /** `'DISCRETE_DEPARTURES'`: no hay frecuencia continua, el detalle real está en `discreteDepartureTimes`. */
  scheduleKind: 'CONTINUOUS_HEADWAY' | 'DISCRETE_DEPARTURES';
  discreteDepartureTimes: string[] | null;
  /** Otras service entities cerradas de la misma línea comercial (variantes/rondines/prolongaciones) sin fila propia — vacío para la mayoría de las líneas. */
  variants: LineSummaryRow[];
  /** Cobertura UNIÓN de esta fila + todas sus `variants` — nunca la suma de `coverage2min`/`coverage5min` por separado. Null cuando `variants` está vacío o mientras el precálculo de grupo no terminó. */
  lineCoverage2min: LineCoverageSummary | null;
  lineCoverage5min: LineCoverageSummary | null;
}

/**
 * Totales del sistema — cada campo trae su propio método de agregación
 * (nunca todos SUM). "Banda seleccionada" reacciona a Día tipo + Banda
 * horaria igual que las filas; "día completo"/"mensual" solo reaccionan a
 * Día tipo (un día completo no cambia porque el usuario mira otra banda
 * horaria del mismo día). Ver `SystemTotals` en el backend
 * (`analysis-summary.types.ts`) para la justificación completa de cada uno.
 */
export interface SystemTotals {
  // Nivel 1 — Dimensionamiento
  /** Flota física asignada = SUMA de maxFleetRequired por línea. Base de "Flota operativa máxima" y de la reserva técnica — el sistema no modela interlining entre líneas. */
  assignedFleetRequired: number;
  /** Métrica analítica separada ("Máximo simultáneo en servicio") — pooling de ciclos entre líneas, puede ser MENOR que assignedFleetRequired. Nunca usar como flota operativa/base de reserva. */
  maxConcurrentVehicles: number | null;
  bandaCritica: { start: string; end: string } | null;
  maxConcurrentVehiclesLineCount: number;
  flotaConReserva: { pct5: number | null; pct10: number | null; pct15: number | null };
  fleetRequiredSystemBand: number | null;
  fleetRequiredSystemBandLineCount: number;

  // Nivel 2 — Producción
  idaKmAcumulado: number;
  vueltaKmAcumulado: number;
  kmOperativosPorDia: Record<DayType, number>;
  serviciosTotalesDia: number;
  horasOperativasTotalesDia: number;
  produccionDiaLineCount: number;
  kmMensualesTotales: number;
  mesCalculado: { year: number; month: number };
  kmAnualesProyectados: number;
  anioCalculado: number;

  // Nivel 3 — Cobertura
  coverage2min: LineCoverageSummary | null;
  coverage5min: LineCoverageSummary | null;
  stopsStatus: 'not_loaded';
  /** Universo real de `TransitStop` del sistema (1735) — nunca una suma de paradas por línea. Null solo si la consulta falló. */
  physicalStopsCount: number | null;

  scenarioFingerprint: string;
}

export interface AnalysisSummaryResponse {
  dayType: DayType;
  band: { start: string; end: string };
  lines: LineSummaryRow[];
  systemTotals: SystemTotals;
}

/**
 * Mapa del sistema — Fase 1, READ ONLY. Solo campos REALMENTE persistidos
 * hoy en `westeer_dev` (ver auditoría de sesión): street-name QA,
 * service-edge QA, clasificación combinada, candidato alternativo y
 * `TransitVariantStop` (uso operativo por línea) todavía no existen en la
 * base — no fabricar esos campos acá, nunca ocultar una parada por
 * carecer de ellos.
 */
export interface SystemMapStopSummary {
  id: string;
  municipalStopId: string | null;
  name: string | null;
  lon: number | null;
  lat: number | null;
  /** Únicos 3 valores reales hoy: 'valid' | 'needs_review' | 'orphaned' — tratar cualquier otro string como "otro/desconocido", nunca asumir que la lista está cerrada. */
  status: string | null;
  edgeId: string | null;
  isActive: boolean;
}

export interface SystemMapStopsResponse {
  stops: SystemMapStopSummary[];
  counts: { total: number; valid: number; needsReview: number; orphaned: number; other: number };
}

export interface SystemMapStopDetail extends SystemMapStopSummary {
  code: string | null;
  type: string | null;
  accessible: boolean | null;
  sourceSystem: string | null;
  declaredRoutes: string[] | null;
  sideOfEdge: string | null;
  positionOnEdge: number | null;
  distanceToNetworkM: number | null;
  /** Punto proyectado REAL persistido — nunca derivado; se usa tal cual para la línea punteada parada→edge actual. */
  projectedCoordinate: [number, number] | null;
  associationMethod: string | null;
  confidenceScore: number | null;
  withinThreshold: boolean | null;
  projectionType: string | null;
  reviewRequiredAt: string | null;
  reviewSource: string | null;
  reviewReason: string | null;
  edgeGeometry: GeoJSON.LineString | GeoJSON.MultiLineString | null;
}

export interface SystemMapRouteGeometry {
  analysisId: string;
  label: string;
  routeMode: 'CIRCUIT' | null;
  ida: GeoJSON.Feature[];
  vuelta: GeoJSON.Feature[] | null;
}

export interface SystemMapRoutesResponse {
  routes: SystemMapRouteGeometry[];
  expected: number;
  rendered: number;
  failed: { analysisId: string; reason: string }[];
}

/** Capa "Red urbana" — `canonical_edges` reales, sin recalcular. `canonical_edges` no tiene columna de nombre de calle: nunca inventarla, `sourceReference`/`routeRef` son lo único disponible. */
export interface UrbanNetworkEdge {
  id: string;
  geometry: GeoJSON.LineString;
  roadClass: string | null;
  sourceReference: string | null;
  routeRef: string | null;
  lengthMeters: number | null;
  oneway: boolean | null;
  onewayDirection: string | null;
  fromNodeId: string;
  toNodeId: string;
}

export interface UrbanNetworkResponse {
  edges: UrbanNetworkEdge[];
  count: number;
}

/**
 * Cliente API de los portales de cliente. Habla exclusivamente con Central
 * — nunca con Transit Suite directamente (ver Fase C, backend-only).
 */
export const clientPortalApi = {
  /** Público — branding y módulos habilitados. Nunca IDs internos. */
  async getConfig(slug: string): Promise<PortalConfig> {
    const response = await axios.get<PortalConfig>(`${API_URL}/portals/${slug}/config`);
    return response.data;
  },

  /** Protegido — 401 sin token, 403 sin entitlement, 200 con entitlement vigente. */
  async checkAccess(slug: string, token: string): Promise<boolean> {
    try {
      await axios.get<{ granted: true }>(`${API_URL}/portals/${slug}/access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return true;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        (error.response?.status === 403 || error.response?.status === 401)
      ) {
        return false;
      }
      throw error;
    }
  },

  /**
   * Datos congelados de un análisis (ej. Línea 500) — nunca se piden ni se
   * publican como archivo estático; siempre vía este endpoint autenticado.
   */
  async getAnalysisBundle(slug: string, analysisId: string, token: string): Promise<unknown> {
    const response = await axios.get<unknown>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/bundle`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async getAnalysisAudit(slug: string, analysisId: string, token: string): Promise<unknown> {
    const response = await axios.get<unknown>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/audit`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** HTML del template del visor, con los placeholders de datos intactos. */
  async getAnalysisTemplate(slug: string, analysisId: string, token: string): Promise<string> {
    const response = await axios.get<string>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/template`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text',
        transformResponse: value => value as string,
      }
    );
    return response.data;
  },

  /**
   * Config vigente de PROGRAMACIÓN + metadata de última edición.
   * `config: null` / `isDefault: true` si nunca hubo un guardado — el
   * visor cae a los defaults congelados que ya tiene client-side.
   */
  async getProgramming(
    slug: string,
    analysisId: string,
    token: string
  ): Promise<ProgrammingConfigResponse> {
    const response = await axios.get<ProgrammingConfigResponse>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/programming`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Guarda una nueva revisión. Nunca envía `userId`/`createdBy`/nombre de
   * usuario — el backend deriva el autor exclusivamente del JWT. En
   * conflicto optimista (409) devuelve `{ok:false, conflict:true,
   * currentRevision}` en vez de tirar, para que el caller pueda mostrar
   * un mensaje claro sin un try/catch propio.
   */
  async saveProgramming(
    slug: string,
    analysisId: string,
    token: string,
    payload: { config: Record<string, unknown>; expectedRevision: number }
  ): Promise<SaveProgrammingResult> {
    try {
      const response = await axios.put<ProgrammingConfigResponse>(
        `${API_URL}/portals/${slug}/analysis/${analysisId}/programming`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { ok: true, data: response.data };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const body = error.response.data as { message?: string; currentRevision?: number | null };
        return {
          ok: false,
          conflict: true,
          currentRevision: body.currentRevision ?? null,
          message: body.message ?? 'La configuración cambió. Recargá para ver la última versión.',
        };
      }

      const message =
        axios.isAxiosError(error) &&
        typeof (error.response?.data as { message?: string } | undefined)?.message === 'string'
          ? (error.response?.data as { message: string }).message
          : 'No se pudo guardar la configuración.';

      return { ok: false, conflict: false, message };
    }
  },

  /** Historial liviano — solo metadata, sin la config completa de cada revisión. */
  async getProgrammingHistory(
    slug: string,
    analysisId: string,
    token: string
  ): Promise<ProgrammingHistoryItem[]> {
    const response = await axios.get<{ items: ProgrammingHistoryItem[] }>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/programming/history`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.items;
  },

  /** Config completa de una revisión puntual del historial, bajo demanda. */
  async getProgrammingHistoryRevision(
    slug: string,
    analysisId: string,
    token: string,
    revision: number
  ): Promise<ProgrammingHistoryDetail> {
    const response = await axios.get<ProgrammingHistoryDetail>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/programming/history/${revision}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** Solo lectura, nunca dispara cálculos — resultado siempre derivado en el momento. */
  async getPedestrianCoverage(
    slug: string,
    analysisId: string,
    token: string,
    params: { walkTimeMinutes: number; walkingSpeedKmh: number }
  ): Promise<PedestrianCoverageResult> {
    const response = await axios.get<PedestrianCoverageResult>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/pedestrian-coverage`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }
    );
    return response.data;
  },

  /** Solo lectura — deriva el edge-set en el servidor a partir del bundle congelado, nunca de IDs enviados por el cliente. */
  async getRouteMetrics(
    slug: string,
    analysisId: string,
    token: string,
    direction: RouteDirection
  ): Promise<RouteMetricsResult> {
    const response = await axios.get<RouteMetricsResult>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/route-metrics`,
      { headers: { Authorization: `Bearer ${token}` }, params: { direction } }
    );
    return response.data;
  },

  /** Solo lectura — proxy hacia SyntaxService, resuelve slug→projectId en el servidor. */
  async getSyntaxMetrics(
    slug: string,
    analysisId: string,
    token: string,
    metric: SyntaxMetricSlimKey
  ): Promise<SyntaxMetricSlimResponse> {
    const response = await axios.get<SyntaxMetricSlimResponse>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/syntax-metrics`,
      { headers: { Authorization: `Bearer ${token}` }, params: { metric } }
    );
    return response.data;
  },

  async getSyntaxRange(slug: string, analysisId: string, token: string): Promise<SyntaxRange> {
    const response = await axios.get<SyntaxRange>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/syntax-range`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** Solo lectura — reusa pedestrian-coverage, nunca dispara un segundo cálculo de isócrona. */
  async getCoverageEntities(
    slug: string,
    analysisId: string,
    token: string,
    direction: RouteDirection,
    params: { walkTimeMinutes: number; walkingSpeedKmh: number }
  ): Promise<CoverageEntitiesResult> {
    const response = await axios.get<CoverageEntitiesResult>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/coverage-entities`,
      { headers: { Authorization: `Bearer ${token}` }, params: { direction, ...params } }
    );
    return response.data;
  },

  /** SERVICIO — secuencia canónica Model C desde la corrida activa persistida. */
  async getModelCServiceStops(
    slug: string,
    analysisId: string,
    token: string,
    direction: RouteDirection
  ): Promise<ModelCServiceStopsResult> {
    const response = await axios.get<ModelCServiceStopsResult>(
      `${API_URL}/portals/${slug}/analysis/${analysisId}/service-stops`,
      { headers: { Authorization: `Bearer ${token}` }, params: { direction } }
    );
    return response.data;
  },

  /**
   * Fila por línea para la tabla resumen inicial del Hub — nunca dispara
   * cálculos que persistan nada, siempre deriva de los mismos servicios
   * (Programación/Cobertura) que ya usa el visor por línea.
   */
  async getAnalysisSummary(
    slug: string,
    token: string,
    dayType: DayType,
    band: { start: string; end: string }
  ): Promise<AnalysisSummaryResponse> {
    const response = await axios.get<AnalysisSummaryResponse>(
      `${API_URL}/portals/${slug}/analysis-summary`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { dayType, band: `${band.start}-${band.end}` },
      }
    );
    return response.data;
  },

  /** Lista liviana de las ~1735 paradas del sistema — un solo request batch, nunca uno por parada. */
  async getSystemMapStops(slug: string, token: string): Promise<SystemMapStopsResponse> {
    const response = await axios.get<SystemMapStopsResponse>(
      `${API_URL}/portals/${slug}/system-map/stops`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** Detalle completo de una parada — bajo demanda, solo al seleccionarla en el mapa. */
  async getSystemMapStopDetail(
    slug: string,
    token: string,
    stopId: string
  ): Promise<SystemMapStopDetail> {
    const response = await axios.get<SystemMapStopDetail>(
      `${API_URL}/portals/${slug}/system-map/stops/${stopId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** Geometría de las 21 service entities cerradas — misma fuente congelada que Programación/TOTAL SISTEMA. */
  async getSystemMapRoutes(slug: string, token: string): Promise<SystemMapRoutesResponse> {
    const response = await axios.get<SystemMapRoutesResponse>(
      `${API_URL}/portals/${slug}/system-map/routes`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** Red urbana completa (~18.900 edges) — se pide una sola vez, perezosamente, solo cuando el usuario activa el toggle "Red urbana". */
  async getSystemMapUrbanNetwork(slug: string, token: string): Promise<UrbanNetworkResponse> {
    const response = await axios.get<UrbanNetworkResponse>(
      `${API_URL}/portals/${slug}/system-map/urban-network`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /** Descarga el escenario configurado (sistema + 17 líneas + 1735 paradas) en el formato pedido — dispara la descarga del navegador directamente, sin navegar a otra pantalla. */
  async downloadAnalysisExport(
    slug: string,
    token: string,
    format: 'xlsx' | 'csv' | 'json'
  ): Promise<void> {
    const response = await axios.get<Blob>(`${API_URL}/portals/${slug}/analysis-export`, {
      params: { format },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = disposition?.match(/filename="([^"]+)"/)?.[1] ?? `sintax-${slug}-export.${format}`;
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
